<template>
  <div class="ChecklistPaneFiles px-6 pt-6" data-locator="files-content">
    <AppTooltip :disabled="currentUserCanUpload" max-width="320">
      <template #activator="{ props }">
        <AppDroppableArea
          class="ChecklistPaneFiles-dropzone text-center pa-5 d-flex flex-column justify-center align-center"
          :class="{'drop-disabled': !currentUserCanUpload}"
          :disabled="!currentUserCanUpload"
          @dragover="dragInProgress = true"
          @dragenter="dragInProgress = true"
          @dragleave="dragInProgress = false"
          @ondrop="getAllFileEntries"
          v-bind="props"
        >
          <app-material-icon
            name="upload"
            size="xl"
          />
          <input
            ref="fileInput"
            class="d-none"
            data-locator="file-input"
            type="file"
            :multiple="true"
            @change="buildAddFiles($event.target.files)"
          />
          <p class="mt-2 text-text-secondary">
            {{ t('dropZoneText1') }}
          </p>
          <app-dropdown-button
            data-locator="import-button"
            type="outlined"
            variant="neutral"
          >
          {{ t('btnImport') }}
            <template #list>
              <v-list data-locator="checklist-attachments-import-menu">
                <app-dropdown-button-item
                  data-locator="from-computer-menu-item"
                  icon="attach_file"
                  :label="t('btnImportFromMyComputer')"
                  @click="onFileInputClick"
                />
                <template v-if="polyOfficeEnabled || imanageEnabledAndLogged">
                  <v-divider />

                  <template v-if="polyOfficeEnabled">
                    <app-dropdown-button-item
                      data-locator="from-poly-office-menu-item"
                      :label="t('importFromPoly')"
                      @click=polyOfficeOpenDialogHandler
                    >
                      <template #icon>
                        <app-icon-poly-office color="#FC5068" />
                      </template>
                    </app-dropdown-button-item>
                  </template>

                  <template v-if="imanageEnabledAndLogged">
                    <app-dropdown-button-item
                      :label="t('importFromImanage')"
                      data-locator="from-imanage-menu-item"
                      @click.stop="iManageFilePickerDialogIsOpen = true"
                    >
                      <template #icon>
                        <v-img src="@/assets/img/iManage_logo.png"
                              max-width="20"
                        />
                      </template>
                    </app-dropdown-button-item>
                  </template>
                </template>
              </v-list>
            </template>
          </app-dropdown-button>
        </AppDroppableArea>
      </template>
      {{ t('dropZoneDisabledTooltip') }}
    </AppTooltip>

    <template v-if="folderId">
      <ChecklistPaneFilesTree
        :mnemo="roomMnemo"
        :folder-id="folderId"
      />
    </template>
    <PolyOfficeDialog
      v-if="polyOfficeEnabled && polyOfficeDialogIsOpen"
      @callback="polyOfficeDialogCallback"
    />
    <IManageFilePickerDialog
      v-model:is-open="iManageFilePickerDialogIsOpen"
      @files="onImanageFiles"
    />
  </div>
</template>


<script>
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex'

import AppDroppableArea from '@/common/AppDroppableArea.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import IManageFilePickerDialog from '@/common/imanage/IManageFilePickerDialog.vue'
import { ChecklistImport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import { FileToUpload } from '@/models/documentToUpload.model'
import { AppUploadHandlerActionTypes } from '@/store/modules/app-upload-handler/action_types'
import { GET_TASK_FOLDER } from '@/store/modules/checklist/action_types'
import {
  RESET_SELECTED_ATTACHED_FILE,
} from '@/store/modules/checklist/mutation_types'
import { LOAD_FOLDERS_TREE, POST_FILDERS_FROM_IMANAGE } from '@/store/modules/documents/action_types'
import { GET_ACTION_INFO, GET_IMPORT_IFRAME } from '@/store/modules/poly-office/action_types'

import ChecklistPaneFilesTree from './ChecklistPaneFilesTree.vue'

export default {
  name: "ChecklistPaneFiles",
  components: {
    AppDroppableArea,
    AppTooltip,
    ChecklistPaneFilesTree,
    IManageFilePickerDialog,
    PolyOfficeDialog,
  },
  setup () {
    return useTranslation({ keyPrefix: 'project.checklist.panes.ChecklistPaneFiles' })
  },
  data () {
    return {
      dragInProgress: false,
      mustRefreshTask: false,
      iManageFilePickerDialogIsOpen: false,
    }
  },
  watch: {
    currentTask: {
      immediate: true,
      handler (val) {
        if(val) {
          this.getTaskFolder()
          this.removeSelectedFiles()
        }
      },
    },
  },
  created() {
    this.LOAD_FOLDERS_TREE()
  },
  computed: {
    ...mapState('checklist', ['currentTaskFolder']),
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('checklist', ['checklistPane']),
    ...mapState('polyOffice', {
      polyOfficeDialogIsOpen: 'dialogIsOpen',
    }),
    ...mapGetters('user', ['polyOfficeEnabled']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('room', ['currentUserRights', 'isCurrentUserPm']),
    currentTask() {
      return this.checklistPane.tasks[0]
    },
    currentUserCanUpload () {
      return this.isCurrentUserPm || this.currentUserRights.canUpload
    },
    folderId() {
      return this.currentTask?.attachmentFolderId
    },
  },
  methods: {
    ...mapActions('appUploadHandler', [AppUploadHandlerActionTypes.ADD_FILES_TO_UPLOAD]),
    ...mapActions('checklist', [GET_TASK_FOLDER]),
    ...mapActions('polyOffice', [
      GET_IMPORT_IFRAME,
      GET_ACTION_INFO,
    ]),
    ...mapActions('documents', [LOAD_FOLDERS_TREE, POST_FILDERS_FROM_IMANAGE]),
    ...mapMutations('checklist', [RESET_SELECTED_ATTACHED_FILE]),
    getTaskFolder () {
      this.GET_TASK_FOLDER({
        mnemo: this.roomMnemo,
        folderId: this.folderId,
        queryObject: { markRecent: true },
      })
    },
    removeSelectedFiles () {
      this.RESET_SELECTED_ATTACHED_FILE()
    },
    buildAddFiles (files) {
      const array = []
      Object.values(files).forEach(file => {
        array.push(new FileToUpload(file, 'checklist', this.currentTaskFolder.id))
      })
      this.ADD_FILES_TO_UPLOAD(array)
      this.$refs.fileInput.value = null
      this.mustRefreshTask = true
    },
    async getFileFromFileEntry (fileEntry, parentId) {
      try {
        return new FileToUpload(await new Promise((resolve, reject) => fileEntry.file(resolve, reject)), 'checklist', parentId)
      } catch (err) {
        console.error(err)
      }
    },
    async readAllDirectoryEntries (directoryReader) {
      const entries = []
      let readEntries = await this.readEntriesPromise(directoryReader)
      while (readEntries.length > 0) {
        entries.push(...readEntries)
        readEntries = await this.readEntriesPromise(directoryReader)
      }
      return entries
    },
    async readEntriesPromise (directoryReader) {
      try {
        return await new Promise((resolve, reject) => {
          directoryReader.readEntries(resolve, reject)
        })
      } catch (err) {
        console.error(err)
      }
    },
    async getAllFileEntries (e) {
      const dataTransferItemList = e.dataTransfer ? e.dataTransfer.items : 'null'
      const fileEntries = []
      const queue = []
      for (let dataTransferItem of dataTransferItemList) {
        queue.push(dataTransferItem.webkitGetAsEntry())
      }
      while (queue.length > 0) {
        const entry = queue.shift()
        if (entry.isFile) {
          fileEntries.push(await this.getFileFromFileEntry(entry, this.currentTaskFolder.id))
        } else if (entry.isDirectory) {
          queue.push(...await this.readAllDirectoryEntries(entry.createReader()))
        }
      }
      this.ADD_FILES_TO_UPLOAD(fileEntries)
      this.mustRefreshTask = true
    },
    onFileInputClick () {
      this.$refs.fileInput.click()
    },
    async polyOfficeOpenDialogHandler () {
      const payload = new ChecklistImport(
        this.roomMnemo,
        this.folderId,
      )
      await this.GET_IMPORT_IFRAME({
        payload,
        successMessage: this.t('polyOffice.importSuccessful'),
      })
    },
    async polyOfficeDialogCallback (data) {
      if (data.status === POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        try {
          // Call get info and check receive status is DONE
          const response = await this.GET_ACTION_INFO()

          if (response.receiveStatus === 'DONE') {
            // Refresh task folder
            this.GET_TASK_FOLDER({
              mnemo: this.roomMnemo,
              folderId: this.folderId,
            })

            this.mustRefreshTask = true
          } else {
            toast.error(this.t('polyOffice.importFailure'))
          }
        } catch (e) {
          toast.error(this.t('polyOffice.importFailure'))
        }
      }
    },
    async onImanageFiles (files) {
      const data = {
        parentId: this.folderId,
        files: [],
      }
      if (files.hasOwnProperty('output_data')) {
        if (files.output_data.hasOwnProperty('selected')) {
          for (const item of files.output_data.selected) {
            if (item.wstype === 'document') {
              data.files.push(item.id)
            }
          }
        }
      }
      try {
        await this.POST_FILDERS_FROM_IMANAGE({
          mnemo: this.roomMnemo,
          data,
        })
        toast.success(this.t('postFildersFromIManageSuccess', data.files.length))
        this.GET_TASK_FOLDER({
          mnemo: this.roomMnemo,
          folderId: this.folderId,
        })
      } catch (error) {
        console.error(error)
        toast.error(this.t('postFildersFromIManageError', data.files.length))
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ChecklistPaneFiles {
  height: 100%;
  overflow-y: auto;
}
.ChecklistPaneFiles-dropzone {
  border: 1px dashed rgb(var(--v-theme-border-secondary));
  border-radius: 12px;
  background-color: rgb(var(--v-theme-surface-secondary));
  gap: 10px;
}
.ChecklistPaneFiles-dropzone.drag-in-progress {
  background-color: rgb(var(--v-theme-surface-info)) !important;
  outline: 1px dashed rgb(var(--v-theme-border-info-bold));
  gap: 10px;
}
.drop-disabled * {
  pointer-events: none;
  color: rgb(var(--v-theme-text-disabled)) !important;
}
</style>
